import React from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Function to format User Retention data for chart
const formatUserRetentionDataForChart = (data, intervalLabel) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const firstItem = data[0];

  // Check if it's simple N-day retention (single row: [total_users, retained_users, retention_rate])
  if (firstItem.length === 3 && typeof firstItem[0] === "number") {
    return [
      {
        name: intervalLabel,
        totalUsers: firstItem[0],
        retainedUsers: firstItem[1],
        retentionRate: firstItem[2],
        label: intervalLabel,
      },
    ];
  }

  // Check if it's year data (4 elements: year, total_users, retained_users, retention_rate)
  if (
    firstItem.length === 4 &&
    typeof firstItem[0] === "number" &&
    firstItem[0] > 1000
  ) {
    return data
      .map((item) => ({
        name: item[0].toString(),
        totalUsers: item[1],
        retainedUsers: item[2],
        retentionRate: item[3],
        label: `${item[0]}`,
      }))
      .reverse(); // Reverse to show chronological order
  }

  // Check if it's timestamp data (4 elements: timestamp, total_users, retained_users, retention_rate)
  if (firstItem.length === 4 && typeof firstItem[0] === "string") {
    return data
      .map((item) => {
        const dateStr = item[0];
        let displayName;

        if (dateStr.includes(" ")) {
          const date = new Date(dateStr);
          if (intervalLabel.includes("Minute")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            });
          } else if (intervalLabel.includes("Hour")) {
            displayName = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              hour12: true,
            });
          } else if (intervalLabel.includes("Week")) {
            displayName = date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          } else {
            displayName = date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          }
        } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const date = new Date(dateStr);
          displayName = date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        } else {
          displayName = dateStr;
        }

        return {
          name: displayName,
          totalUsers: item[1],
          retainedUsers: item[2],
          retentionRate: item[3],
          label: displayName,
        };
      })
      .reverse();
  }

  return [];
};

// Custom Tooltip for User Retention Chart
const UserRetentionTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "12px",
          fontSize: "12px",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#2d3748" }}>
          {label}
        </p>
        <p style={{ margin: "4px 0", color: "#667eea" }}>
          <strong>Total Users:</strong> {data.totalUsers?.toLocaleString() || 0}
        </p>
        <p style={{ margin: "4px 0", color: "#48bb78" }}>
          <strong>Retained Users:</strong>{" "}
          {data.retainedUsers?.toLocaleString() || 0}
        </p>
        {data.retentionRate !== null && data.retentionRate !== undefined && (
          <p
            style={{
              margin: "4px 0",
              color: "#f59e0b",
              fontWeight: "600",
            }}
          >
            <strong>Retention Rate:</strong> {data.retentionRate.toFixed(2)}%
          </p>
        )}
      </div>
    );
  }
  return null;
};

// Component to render User Retention Chart
const UserRetentionChart = ({ data, interval }) => {
  const chartData = formatUserRetentionDataForChart(data, interval);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-placeholder">
        📊 No data available
        <small>Try selecting a different date range</small>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <ComposedChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="name"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          angle={chartData.length > 10 ? -45 : 0}
          textAnchor={chartData.length > 10 ? "end" : "middle"}
          height={chartData.length > 10 ? 60 : 30}
        />
        <YAxis
          yAxisId="left"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
          label={{
            value: "Users",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: "11px" },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="#f59e0b"
          style={{ fontSize: "12px" }}
          domain={[0, 100]}
          label={{
            value: "Retention %",
            angle: 90,
            position: "insideRight",
            style: { fontSize: "11px" },
          }}
        />
        <Tooltip content={<UserRetentionTooltip />} />
        <Legend wrapperStyle={{ fontSize: "12px" }} />
        <Bar
          yAxisId="left"
          dataKey="totalUsers"
          fill="#667eea"
          name="Total Users"
          radius={[8, 8, 0, 0]}
        />
        <Bar
          yAxisId="left"
          dataKey="retainedUsers"
          fill="#48bb78"
          name="Retained Users"
          radius={[8, 8, 0, 0]}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="retentionRate"
          stroke="#f59e0b"
          strokeWidth={3}
          dot={{ fill: "#f59e0b", r: 5 }}
          activeDot={{ r: 7 }}
          name="Retention Rate %"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default UserRetentionChart;
